import React, { useState, useRef, useCallback } from 'react';
import { Camera, Upload, FileText, X, Check, RotateCcw, ZoomIn, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

export function ScanSignedConsentModal({ isOpen, onClose, consent, onSuccess, toast }) {
  const [step, setStep] = useState('choose'); // choose, camera, preview
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();
    setCapturedImage(null);
    setCapturedFile(null);
    setStep('choose');
    onClose();
  }, [stopCamera, onClose]);

  const startCamera = async () => {
    setStep('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast?.({ title: "Camera Error", description: "Could not access camera. Please use 'Choose File' instead.", variant: "destructive" });
      setStep('choose');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    stopCamera();

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `signed_consent_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setCapturedFile(file);
        setCapturedImage(canvas.toDataURL('image/jpeg', 0.9));
        setStep('preview');
      }
    }, 'image/jpeg', 0.9);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast?.({ title: "File Too Large", description: "Maximum file size is 25 MB.", variant: "destructive" });
      return;
    }
    setCapturedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCapturedImage(ev.target.result);
        setStep('preview');
      };
      reader.readAsDataURL(file);
    } else {
      // PDF or other non-image — show file info instead of preview
      setCapturedImage(null);
      setStep('preview');
    }
  };

  const handleUpload = async () => {
    if (!capturedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', capturedFile);
      const token = localStorage.getItem('dentalToken');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/informed-consent/upload-signed/${consent.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast?.({ title: "Saved to Patient Record", description: `Signed consent for "${consent.consentName}" has been uploaded and saved.` });
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast?.({ title: "Upload Failed", description: err.response?.data?.detail || "Could not save the file. Please try again.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const retake = () => {
    setCapturedImage(null);
    setCapturedFile(null);
    setStep('choose');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="scan-consent-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">Scan Signed Consent</h2>
            <p className="text-blue-200 text-sm mt-0.5">{consent?.consentName || 'Informed Consent'}</p>
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors" data-testid="scan-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Step 1: Choose method */}
          {step === 'choose' && (
            <div data-testid="scan-choose-method">
              <p className="text-gray-600 text-sm mb-5 text-center">
                Choose how to capture the signed consent document:
              </p>
              <div className="grid grid-cols-1 gap-3">
                <button
                  data-testid="scan-take-photo-btn"
                  onClick={startCamera}
                  className="flex items-center gap-4 p-4 border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Camera className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Take Photo</p>
                    <p className="text-sm text-gray-500">Use your device camera to photograph the signed document</p>
                  </div>
                </button>

                <button
                  data-testid="scan-choose-file-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <Upload className="h-6 w-6 text-gray-600 group-hover:text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Choose File</p>
                    <p className="text-sm text-gray-500">Upload a scan or image from your device</p>
                  </div>
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.heic,.heif,.bmp,.tiff,.tif,.webp"
                capture={undefined}
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-xs text-gray-500 text-center">
                  <strong>Accepted:</strong> JPG, PNG, HEIC, PDF, GIF, BMP, TIFF, WEBP &nbsp;|&nbsp; <strong>Max size:</strong> 25 MB
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Camera view */}
          {step === 'camera' && (
            <div data-testid="scan-camera-view">
              <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                {/* Capture guide overlay */}
                <div className="absolute inset-4 border-2 border-dashed border-white/40 rounded-lg pointer-events-none" />
                <p className="absolute bottom-2 left-0 right-0 text-center text-white/70 text-xs">
                  Position the signed document within the frame
                </p>
              </div>
              <div className="flex items-center justify-center gap-3 mt-4">
                <Button variant="outline" onClick={() => { stopCamera(); setStep('choose'); }} data-testid="scan-camera-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={capturePhoto}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full text-base font-semibold"
                  data-testid="scan-capture-btn"
                >
                  <Camera className="h-5 w-5 mr-2" />
                  Capture
                </Button>
              </div>
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          {/* Step 3: Preview and confirm */}
          {step === 'preview' && (
            <div data-testid="scan-preview-view">
              <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 mb-4">
                {capturedImage ? (
                  <img src={capturedImage} alt="Captured signed consent" className="w-full max-h-80 object-contain" />
                ) : (
                  <div className="flex flex-col items-center justify-center py-10">
                    <FileText className="h-12 w-12 text-blue-500 mb-2" />
                    <p className="font-medium text-gray-700">{capturedFile?.name}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {(capturedFile?.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800 text-center">
                  This will save the signed consent to <strong>{consent?.patientName || "the patient"}</strong>'s record and mark it as <strong>"Signed (In-Office)"</strong>.
                </p>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Button variant="outline" onClick={retake} className="flex-1" data-testid="scan-retake-btn">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retake
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                  data-testid="scan-save-btn"
                >
                  {uploading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/50 border-t-white rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Save to Patient Record
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
